import {
  EXPERIMENTS,
  getWixDataCategoryId,
  getWixDataTagId,
  handleAggregatorResponseWithHeaders,
  isExperimentEnabled,
  postListWidgetStyleParams as params,
} from '@wix/communities-blog-client-common';
import setPosts from '../../common/actions/set-posts';
import { createPromisifiedAction } from '../../common/actions-promisifier/create-promisified-action';
import { getAppSettingsValue } from '../../common/selectors/app-settings-base-selectors';
import { getQueryLocale } from '../../common/selectors/locale-selectors';
import { getDemoPosts } from '../../common/services/demo-posts';
import getEnvironment from '../../common/services/get-environment';
import { getTotalResults } from '../../common/services/pagination';
import timezoneService from '../../common/services/timezone';
import { getTimezone } from '../../common/store/basic-params/basic-params-selectors';
import { handleCategoriesResponse } from '../../common/store/categories/fetch-categories';
import { getInstanceId } from '../../common/store/instance-values/instance-values-selectors';
import { setIsLoading } from '../../common/store/is-loading/is-loading-actions';
import { setPostCount } from '../../common/store/post-count/set-posts-count';
import { fetchTopology } from '../../common/store/topology/topology-actions';
import { handleTranslationsResponse } from '../../common/store/translations/translations-actions';
import { AppState } from '../../common/types';
import { getPostListWidgetPageSize } from '../selectors/post-list-widget-page-size';
import { AggregatorRequest } from '../types';
import { PostListWidgetThunkAction } from '../types/post-list-widget-thunk-args';

interface FetchPostListRenderModelParams {
  aggregatorRequest: AggregatorRequest;
  state: AppState;
  page?: number;
  fields?: string[];
}

const fetchPostListRenderModel = ({
  aggregatorRequest,
  state,
  page,
  fields,
}: FetchPostListRenderModelParams) => {
  const postLimit = getPostListWidgetPageSize(state);
  const featuredOnly = getAppSettingsValue({
    state,
    key: `style.booleans.${params.postListWidgetIsFeatured.key}`,
  });
  const categoryId = getWixDataCategoryId(state);
  const tagId = getWixDataTagId(state);
  const language = getQueryLocale(state);
  const timezone = getTimezone(state) || timezoneService.timezone();

  return aggregatorRequest<any>(
    `/v1/post-list-widget/render-model?${[
      `timezone=${timezone}`,
      postLimit && `postLimit=${postLimit}`,
      featuredOnly && `featuredOnly=${featuredOnly}`,
      categoryId && `categoryId=${categoryId}`,
      tagId && `tagId=${tagId}`,
      language && `language=${language}`,
      page && `page=${page}`,
      fields && `fields=${fields.join(',')}`,
    ]
      .filter(Boolean)
      .join('&')}`,
    { throwOnInvalidJson: true },
  );
};

const handlePostListPostsResponse =
  (postsResponse: any, { page = 1 } = {}): PostListWidgetThunkAction =>
  (dispatch, getState, { wixCodeApi, httpClient, aggregatorRequest }) => {
    dispatch(setIsLoading('postListPosts', undefined, true));

    return dispatch(handleAggregatorResponseWithHeaders(postsResponse))
      .then(async ({ body, headers }) => {
        if (!body.length && getEnvironment(wixCodeApi).isEditorSegment) {
          const state = getState();
          const fake = await getDemoPosts({
            httpClient,
            aggregatorRequest,
            getState,
            dispatch,
            wixCodeApi,
            query: {
              page,
              pageSize: getPostListWidgetPageSize(state),
              featuredOnly: getAppSettingsValue({
                state,
                key: `style.booleans.${params.postListWidgetIsFeatured.key}`,
              }),
              categoryId: getWixDataCategoryId(state),
              tagId: getWixDataTagId(state),
              language: getQueryLocale(state),
            },
          });
          body = fake.posts;
          headers = fake.headers as any;
        }
        dispatch(setPosts(body));
        dispatch(setPostCount(getTotalResults(headers)));
      })
      .then(() => dispatch(setIsLoading('postListPosts', undefined, false)))
      .catch(() => dispatch(setIsLoading('postListPosts', undefined, false)));
  };

export const fetchInitialData =
  (): PostListWidgetThunkAction =>
  async (
    dispatch,
    getState,
    { aggregatorRequest, compId, flowAPI, wixCodeApi },
  ) => {
    const state = getState();
    const instanceId = getInstanceId(state);
    const warmupDataKey = `post-list-${compId}`;
    const shouldUseWarmupData =
      flowAPI.environment.isSSR &&
      isExperimentEnabled(state, EXPERIMENTS.USE_WARMUP_STATE_IN_POST_LIST);

    let data;

    if (shouldUseWarmupData) {
      data = await fetchPostListRenderModel({
        aggregatorRequest,
        state,
      });
      wixCodeApi.window.warmupData.set(warmupDataKey, data);
    } else {
      data =
        wixCodeApi.window.warmupData.get(warmupDataKey) ??
        (await fetchPostListRenderModel({
          aggregatorRequest,
          state,
        }));
    }

    await dispatch(handleTranslationsResponse(data.translations));

    if (data.categories) {
      await dispatch(handleCategoriesResponse(data.categories));
    }

    await dispatch(fetchTopology(instanceId));
    await dispatch(handlePostListPostsResponse(data.posts));
  };

export const fetchPostListPosts =
  (page?: number): PostListWidgetThunkAction =>
  async (dispatch, getState, { aggregatorRequest }) => {
    const state = getState();

    const { posts } = await fetchPostListRenderModel({
      aggregatorRequest,
      state,
      page,
      fields: ['posts'],
    });

    await dispatch(handlePostListPostsResponse(posts, { page }));
  };

export const fetchPostListPostsPromisified = createPromisifiedAction(
  fetchPostListPosts,
  () => null,
  (response) => response.status,
);
